/**
 * File: useAgentJobs.ts
 * Description: Public hooks over the navigation-resilient agent-job tracker.
 *   Pages use these to start tracked jobs, read live status for a target, and
 *   react when a job finishes (including while the page was unmounted).
 */

import {
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import {
  StartJobInput,
  useAgentJobsManager,
} from '../contexts/AgentJobsContext';
import { AGENT_JOB_COMPLETED_EVENT } from '../services/agentJobTargets';
import { AgentJobDescriptor, isTerminalStatus } from '../types/agentJob';

/** True while a descriptor is pending or running. */
export const isJobActive = (
  descriptor: AgentJobDescriptor | undefined,
): descriptor is AgentJobDescriptor =>
  !!descriptor && !isTerminalStatus(descriptor.status);

/** Imperative API: start tracking a job, or stop tracking one. */
export const useAgentJobsApi = (): {
  startJob: (input: StartJobInput) => Promise<string>;
  startJobAndWait: <TResult = unknown>(
    input: StartJobInput,
  ) => Promise<TResult>;
  cancelTracking: (jobId: string) => void;
} => {
  const manager = useAgentJobsManager();
  return useMemo(
    () => ({
      startJob: (input: StartJobInput) => manager.startJob(input),
      startJobAndWait: <TResult = unknown>(input: StartJobInput) =>
        manager.startJobAndWait<TResult>(input),
      cancelTracking: (jobId: string) => manager.cancelTracking(jobId),
    }),
    [manager],
  );
};

/** The tracked job for a given project + target, if any. */
export const useAgentJob = (
  projectId: number,
  target: string,
): AgentJobDescriptor | undefined => {
  const manager = useAgentJobsManager();
  const map = useSyncExternalStore(manager.subscribe, manager.getSnapshot);
  return useMemo(
    () =>
      Object.values(map).find(
        (job) => job.projectId === projectId && job.target === target,
      ),
    [map, projectId, target],
  );
};

/** Every tracked job for a project (for multi-target pages). */
export const useAgentJobs = (projectId: number): AgentJobDescriptor[] => {
  const manager = useAgentJobsManager();
  const map = useSyncExternalStore(manager.subscribe, manager.getSnapshot);
  return useMemo(
    () => Object.values(map).filter((job) => job.projectId === projectId),
    [map, projectId],
  );
};

/**
 * Run `onDone` whenever a tracked job that matches `predicate` reaches a
 * terminal state — fires even if the originating page was unmounted when the
 * job finished, because completion is broadcast on a window event.
 */
export const useAgentJobCompletion = (
  predicate: (descriptor: AgentJobDescriptor) => boolean,
  onDone: (descriptor: AgentJobDescriptor) => void,
): void => {
  const predicateRef = useRef(predicate);
  const onDoneRef = useRef(onDone);
  predicateRef.current = predicate;
  onDoneRef.current = onDone;

  useEffect(() => {
    const handler = (event: Event): void => {
      const descriptor = (event as CustomEvent<AgentJobDescriptor>).detail;
      if (descriptor && predicateRef.current(descriptor)) {
        onDoneRef.current(descriptor);
      }
    };
    globalThis.addEventListener(AGENT_JOB_COMPLETED_EVENT, handler);
    return () =>
      globalThis.removeEventListener(AGENT_JOB_COMPLETED_EVENT, handler);
  }, []);
};
